// The eight layers that write flavour into a wine, shared by the Layers
// reference page and the Taste-a-wine analysis. Order tells the story from
// vine to glass; ids match the analyser's source enum.
export const LAYERS = [
  {
    id: "variety",
    label: "Variety",
    color: "#c04a58",
    essence: "The genetic script — what the grape is able to say at all.",
    body: "Every variety carries a fixed repertoire of aroma compounds and structural tendencies: Sauvignon Blanc's pyrazines and thiols, Muscat's terpenes, Nebbiolo's tannin and rose. Nothing later in the chain can add a note the genetics can't produce; everything later decides which parts of the script get read aloud. Clonal variation is a real sub-layer here (Pinot clone 777 does not taste like Pommard), though this app treats each variety as one voice.",
    listen_for: ["blackcurrant", "rose", "bell-pepper", "lychee", "black-pepper"],
    compounds: ["ibmp", "linalool", "rotundone", "3mh", "beta-damascenone"],
  },
  {
    id: "terroir",
    label: "Terroir",
    color: "#7d93a8",
    essence: "The stable signature of the place — what the site always says.",
    body: "Soil, geology, aspect, altitude and climate leave a consistent imprint vintage after vintage: maritime salinity in Albariño, flint in Pouilly-Fumé, the savoury minerality of volcanic sites. Terroir rarely invents notes outright — it tilts the balance, framing the variety's fruit with stone, salt and earth, and setting the acidity the site can keep.",
    listen_for: ["salinity", "flint", "wet-stone", "chalk", "iodine"],
    compounds: ["benzenemethanethiol", "malic-acid", "tartaric-acid"],
  },
  {
    id: "vineyard",
    label: "Vineyard & vintage",
    color: "#7a9a4c",
    essence: "The season and the choices — where on its spectrum the grape lands.",
    body: "Picking date, yields, canopy, irrigation, and what the year's weather did. This is the single biggest lever on varietal expression: the same Cabernet block gives green bell-pepper at one picking date and cassis liqueur three weeks later. Where terroir is what a place always says, vintage is what this year did to it — cool years keep pyrazines and acid, hot years trade them for jam and alcohol.",
    listen_for: ["bell-pepper", "grass", "plum-jam", "raisin", "blackcurrant-leaf"],
    compounds: ["ibmp", "cis-3-hexenol", "malic-acid"],
  },
  {
    id: "fruit-state",
    label: "Fruit state",
    color: "#d78f3c",
    essence: "Transformed fruit — when the berries change before the winery does anything.",
    body: "Botrytis threads honey, saffron and beeswax through Sauternes and Tokaji while concentrating sugar and acid. Drying fruit for Amarone or Vin Santo turns cherry into fig and prune. Freezing it on the vine makes ice wine. These transformations create flavours no fresh berry contains, and they happen at the boundary between vineyard and winery.",
    listen_for: ["honey", "saffron", "beeswax", "fig", "raisin", "prune"],
    compounds: ["sotolon", "furaneol", "gamma-nonalactone"],
  },
  {
    id: "ferment",
    label: "Ferment",
    color: "#cbb85a",
    essence: "The microbial rewrite — yeast and bacteria as co-authors.",
    body: "Yeast doesn't just make alcohol: it manufactures esters (banana, pear drop, red berry), unlocks varietal thiols from odourless precursors, and sets the tone of the whole wine. Malolactic bacteria trade tart malic acid for cream and butter. Technique lives here too — carbonic maceration's bubblegum in Beaujolais, skin-contact phenolics in orange wine, the savoury breadth of wild ferments.",
    listen_for: ["banana", "bubblegum", "butter", "cream", "strawberry"],
    compounds: ["isoamyl-acetate", "diacetyl", "3mha", "2-phenylethanol"],
  },
  {
    id: "elevage",
    label: "Élevage",
    color: "#a8703f",
    essence: "The upbringing — vessel, lees and time before bottle.",
    body: "Oak contributes vanilla, toast, coconut and spice, and its slow oxygen softens tannin. Lees stirring builds brioche and cream through autolysis. Flor yeast in fino and vin jaune adds green almond and curry-leaf sharpness; deliberate oxidative maturation makes tawny and Madeira. Concrete and amphora shape texture while staying nearly silent aromatically — élevage is much bigger than oak.",
    listen_for: ["vanilla", "toast", "coconut", "brioche", "almond", "smoke"],
    compounds: ["vanillin", "cis-oak-lactone", "guaiacol", "sotolon", "acetaldehyde"],
  },
  {
    id: "age",
    label: "Bottle age",
    color: "#9a6a54",
    essence: "Time's edit — primary fruit recedes, tertiary character emerges.",
    body: "In bottle, slow chemistry converts fruit into something else entirely: Riesling develops petrol as TDN forms, reds turn to leather, truffle, tobacco and forest floor, whites deepen into honey and hazelnut. The closure is part of this layer — screwcaps age wines slowly and reductively, corks admit whispers of oxygen, and the difference is tastable within a decade.",
    listen_for: ["petrol", "leather", "truffle", "tobacco", "forest-floor", "hazelnut"],
    compounds: ["tdn", "vitispirane", "sotolon", "dms"],
  },
  {
    id: "fault",
    label: "Fault",
    color: "#a2362a",
    essence: "The uninvited voices — microbes and chemistry gone wrong.",
    body: "Brettanomyces writes barnyard and band-aid over fruit with 4-ethylphenol. Acetic bacteria push vinegar and nail-polish notes. TCA from tainted cork mutes everything into damp cardboard. Light strike, mousiness and runaway reduction each have their own signature. In small doses a few of these read as complexity — one taster's fault is another's funk — which is exactly why they deserve their own layer rather than silence.",
    listen_for: ["barnyard", "game", "balsamic"],
    compounds: ["4-ethylphenol", "4-ethylguaiacol", "acetic-acid", "ethyl-acetate", "dms"],
  },
]

export const layerById = id => LAYERS.find(l => l.id === id)

// legacy enum values the model might still emit
export const normaliseSource = s =>
  ({ oak: "elevage", fermentation: "ferment", aging: "age" }[s] || s)
